import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CircleAlert, Folder, Tag, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { updateCourse, getCourse } from '../../api/courses';
import Dropdown from '../../components/Dropdown/Dropdown';
import LoadingState from '../../components/LoadingState/LoadingState';
import { useI18n } from '../../i18n/I18nContext';
import './CourseCategories.css';

// Values sent to/from the backend stay English; only the displayed label is localized.
const PRESET_CATEGORIES = [
  { value: 'Language', labelKey: 'catLanguage' },
  { value: 'Mathematics', labelKey: 'catMathematics' },
  { value: 'Science', labelKey: 'catScience' },
  { value: 'Programming', labelKey: 'catProgramming' },
  { value: 'Design', labelKey: 'catDesign' },
  { value: 'Business', labelKey: 'catBusiness' },
  { value: 'Marketing', labelKey: 'catMarketing' },
  { value: 'Music', labelKey: 'catMusic' },
  { value: 'Art', labelKey: 'catArt' },
  { value: 'History', labelKey: 'catHistory' },
  { value: 'Other', labelKey: 'catOther' },
];

export default function CourseCategories({ courseId }) {
  const { t } = useI18n();
  const [course, setCourse] = useState(null);
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // What the course holds on the server: a save sends only what differs from it.
  const savedRef = useRef({ category: '', tags: [] });
  const tagInputRef = useRef(null);

  const fetchCourse = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      // getCourse already returns the course itself (the API client unwraps the envelope).
      const c = (await getCourse(courseId)) || {};
      setCourse(c);
      setCategory(c.category || '');
      setTags(c.tags || []);
      savedRef.current = { category: c.category || '', tags: c.tags || [] };
    } catch {
      // Without the stored values the form would show an empty category and tag list, and saving
      // it would wipe them, so offer a retry instead of the form.
      setLoadFailed(true);
      toast.error(t('courseInsights.categories.loadFailed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => { fetchCourse(); }, [fetchCourse]);

  // The preview must show the same localized label as the Dropdown, not the raw English preset
  // value stored on the course. Falls back to the raw value for a custom category that isn't
  // one of the presets (nothing to translate in that case).
  const getCategoryLabel = useCallback((value) => {
    const preset = PRESET_CATEGORIES.find((c) => c.value === value);
    return preset ? t(`courseOverview.${preset.labelKey}`) : value;
  }, [t]);

  const handleSave = async () => {
    // Send only what the user changed, so a field they did not touch is never overwritten.
    const saved = savedRef.current;
    const body = {};
    if (category !== saved.category) body.category = category || null;
    if (tags.length !== saved.tags.length || tags.some((tag, i) => tag !== saved.tags[i])) body.tags = tags;
    if (Object.keys(body).length === 0) {
      setDirty(false);
      return;
    }
    setSaving(true);
    try {
      await updateCourse(courseId, body);
      savedRef.current = { category, tags };
      toast.success(t('courseInsights.categories.saved'));
      setDirty(false);
    } catch {
      toast.error(t('courseInsights.categories.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    setTags((prev) => [...prev, trimmed]);
    setTagInput('');
    setDirty(true);
  };

  const removeTag = (tag) => {
    setTags((prev) => prev.filter((tg) => tg !== tag));
    setDirty(true);
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addTag(); }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      setTags((prev) => prev.slice(0, -1));
      setDirty(true);
    }
  };

  if (loading) {
    return (
      <div className="ui-stack ctg-page">
        <LoadingState skeleton="form" rows={3} message={t('courseInsights.categories.loading')} />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="ui-stack ctg-page">
        <div className="ui-alert" role="alert">
          <CircleAlert aria-hidden="true" />
          <p>{t('courseInsights.categories.loadFailed')}</p>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={fetchCourse}>
            {t('ui.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ui-stack ctg-page">
      <section className="ctg-section">
        <div className="ctg-head">
          <span className="ui-icon-chip ui-icon-chip--md" data-tone="brand"><Folder aria-hidden="true" /></span>
          <div>
            <h4 className="ctg-title">{t('courseInsights.categories.categoryTitle')}</h4>
            <p className="ctg-desc">{t('courseInsights.categories.categoryDesc')}</p>
          </div>
        </div>
        <div className="ui-field ctg-field">
          {/* The section above already says what this is: the field is named for screen readers */}
          <Dropdown
            value={category}
            onChange={(val) => { setCategory(val); setDirty(true); }}
            options={[
              { value: '', label: t('courseInsights.categories.noCategory') },
              ...PRESET_CATEGORIES.map((c) => ({ value: c.value, label: t(`courseOverview.${c.labelKey}`) })),
              // A category stored outside the presets (e.g. 'languages') still shows as selected.
              ...(category && !PRESET_CATEGORIES.some((c) => c.value === category)
                ? [{ value: category, label: category }]
                : []),
            ]}
            placeholder={t('courseInsights.categories.categoryPlaceholder')}
            ariaLabel={t('courseInsights.categories.categoryLabel')}
          />
        </div>
      </section>

      <section className="ctg-section">
        <div className="ctg-head">
          <span className="ui-icon-chip ui-icon-chip--md" data-tone="brand"><Tag aria-hidden="true" /></span>
          <div>
            <h4 className="ctg-title">{t('courseInsights.categories.tagsTitle')}</h4>
            <p className="ctg-desc">{t('courseInsights.categories.tagsDesc')}</p>
          </div>
        </div>
        <div className="ui-field ctg-field">
          {/* The section above already says "Teglar": the label is for screen readers only */}
          <label className="visually-hidden" htmlFor="ctg-tag-input">{t('courseInsights.categories.tagsLabel')}</label>
          {/* Anywhere in the field starts typing the next tag */}
          <div
            className="ctg-tags"
            onClick={(e) => { if (e.target === e.currentTarget) tagInputRef.current?.focus(); }}
          >
            {tags.map((tag) => (
              <span key={tag} className="ui-chip ui-chip--removable ctg-tag">
                {tag}
                <button
                  type="button"
                  className="ui-chip__x"
                  onClick={() => removeTag(tag)}
                  aria-label={t('courseInsights.categories.removeTag', { tag })}
                >
                  <X aria-hidden="true" />
                </button>
              </span>
            ))}
            <input
              ref={tagInputRef}
              id="ctg-tag-input"
              className="ctg-tag-input"
              data-no-style
              type="text"
              placeholder={tags.length === 0 ? t('courseInsights.categories.tagInputPlaceholderEmpty') : t('courseInsights.categories.tagInputPlaceholderMore')}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
            />
          </div>
          <p className="ui-hint">{t('courseInsights.categories.tagsHint')}</p>
        </div>

        {course && (
          <div className="section-card section-card--sunken ctg-preview">
            <h3 className="ctg-preview-title">{t('courseInsights.categories.previewTitle')}</h3>
            <p className="ctg-preview-name">{course.title}</p>
            <div className="ctg-preview-meta">
              {category && <span className="ds-badge ds-badge-brand">{getCategoryLabel(category)}</span>}
              {tags.map((tag) => (
                <span key={tag} className="ds-badge ds-badge-neutral">{tag}</span>
              ))}
              {!category && tags.length === 0 && (
                <span className="ctg-preview-empty">{t('courseInsights.categories.previewEmpty')}</span>
              )}
            </div>
          </div>
        )}
      </section>

      <div className="ctg-actions">
        {dirty && <span className="ds-badge ds-badge-warning">{t('courseInsights.categories.unsaved')}</span>}
        {/* Yellow only while there is something to save: a dimmed yellow button reads as broken */}
        <button
          type="button"
          className={`ds-btn ${dirty ? 'ds-btn-primary' : 'ds-btn-secondary'}${saving ? ' is-loading' : ''}`}
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          <span>{saving ? t('courseInsights.categories.saving') : t('courseInsights.categories.saveChanges')}</span>
        </button>
      </div>
    </div>
  );
}
